// Copyright (c) StrangeBee 2024

import { ICONS } from '@Constants/iconsApp';
import { classList } from '@Helpers/className';
import { formatTags } from '@Helpers/tags';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import { TaxonomyTagSelect } from '@Organisms/Taxonomy';
import { SelectProps } from 'antd';
import React, { ReactElement, ReactNode, useContext, useState } from 'react';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { ScopeContext } from '@Context/ScopeProvider';
import { Autocomplete } from './Autocomplete';

import './THTagSelector.scss';

export interface THTagSelectorProps extends SelectProps<any> {
    borderless?: boolean;
    taxonomies?: boolean;
    suffix?: ReactNode;
}

export const THTagSelector = ({
    borderless,
    className,
    onChange,
    value,
    disabled,
    taxonomies = true,
    suffix,
    ...props
}: THTagSelectorProps): ReactElement => {
    const [isHover, setIsHover] = useState(false);
    const { coloredTags } = useContext(ScopeContext);

    const {
        taxonomy: { list: taxonomyList = [] },
    } = useContext(ScopeContext);

    return (
        <div
            className={classList('th-tag-selector__container', {
                'no-border': borderless,
                'no-taxonomies': !taxonomies || !taxonomyList.length,
                suffix,
            })}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
        >
            <THSelect
                mode="tags"
                borderless={borderless}
                className={classList('th-tag-selector', className, { disabled })}
                tagRender={({ label, onClose }): ReactElement => (
                    <DisplayTag
                        tag={label as string}
                        onClick={() => !disabled && onClose()}
                        color={coloredTags[label as string]}
                    />
                )}
                value={value}
                onChange={onChange}
                disabled={disabled}
                {...props}
            />

            {!disabled && taxonomies && taxonomyList.length > 0 && (
                <TaxonomyTagSelect
                    triggerElem={
                        <THButton
                            className={classList('th-tag-selector__button', {
                                hovered: isHover,
                                'no-border': borderless,
                            })}
                            type="primary"
                            icon={<ICONS.PlusIcon />}
                            color="gray"
                        />
                    }
                    onClick={(newTags): void => {
                        const newValues = Array.from(
                            new Set([...(value || []), ...newTags.map((tag) => formatTags(tag))]),
                        );
                        (onChange as any)?.(newValues);
                    }}
                />
            )}

            {suffix && <div className="th-tag-selector__suffix">{suffix}</div>}
        </div>
    );
};

THTagSelector.Autocomplete = Autocomplete;
